import React, { useEffect } from 'react';
import ProgramCard from '@/components/Layout/ProgramCard/ProgramCard';
import { DebtConsolidation } from '@/components/Layout/DebtConsolidation/DebtConsolidation';
import ProgramSelector from '@/components/ProgramSelector/ProgramSelector';
import CalculateAmount from '@/components/CalculateAmount/CalculateAmount';
import { CardsContainer, SubtitleContainer } from './VmoV2.styles';
import { Wrapper } from '../wrapper.styles';
import { TextBlock, TypeOfText } from '@/components/text/TextBlock';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import { NavigationButton, PageOrigin } from '@components/Button/NavigationButton/NavigationButton';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { FooterNewVmo } from '@/components/FooterNewVmo/FooterNewVmo';
import { ConfirmationModal } from '@/components/Modals/ConfirmModal/ConfirmModal';
import { pages } from '@/defaultVerbiages';
import { useDataChangeDetection } from '@/hooks/useDataChangeDetection';
import {
  setOpenCreditUnionModal,
  setIsCreditUnion,
  setAcceptedCreditUnion,
  setOpenDebtConsolidationModal,
  setAcceptedDebtConsolidation,
  setSelectedProgram,
  setHasSeenDebtConsolidationModal,
  setHasSeenCUModal,
  clearSelectedProgramStatus,
  clearCheckboxes,
} from '@/reducers/VmoV2';

interface VmoV2Props {
  onAccess: (page: string) => void;
}

const VmoV2: React.FC<VmoV2Props> = ({ onAccess }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const allowDebtConsolidation = useAppSelector((state) => state.TemplateConfig?.features?.allowDebtConsolidation?.isEnabled ?? false);

  const { hasDataChanged, resetChangeDetection } = useDataChangeDetection(['selectedProgram']);
  const {
    isReprice,
    isCreditUnion,
    openCreditUnionModal,
    acceptedCreditUnion,
    hasSeenCUModal,
    openDebtConsolidationModal,
    acceptedDebtConsolidation,
    hasSeenDebtConsolidationModal,
    selectedProgram,
  } = useAppSelector((state) => state.VmoV2);

  const hasTradelines = (loanApplication?.borrowers?.[0]?.tradelines?.length ?? 0) > 0;

  // handle program selection
  useEffect(() => {
    const hasManualSelection = (selectedProgram?.length || 0) > 0;
    if (hasManualSelection && loanApplication?.selectedProgram?.programName) {
      const isCUProgram = loanApplication?.selectedProgram?.programName?.includes('Credit Union');
      dispatch(setIsCreditUnion(isCUProgram));

      if (!isReprice && isCUProgram && !hasSeenCUModal && !openCreditUnionModal) {
        dispatch(setOpenCreditUnionModal(true));
      } else if (!isReprice && !isCUProgram && !hasSeenDebtConsolidationModal && hasTradelines && !openDebtConsolidationModal) {
        dispatch(setOpenDebtConsolidationModal(true));
      }
    } else if (!loanApplication?.selectedProgram?.programName) {
      dispatch(clearCheckboxes());
      dispatch(clearSelectedProgramStatus());
    }
  }, [loanApplication?.selectedProgram?.programName]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // handle Credit Union modal response
  const handleCreditUnionModalClose = (confirmed: boolean) => {
    dispatch(setHasSeenCUModal(true));
    dispatch(setAcceptedCreditUnion(confirmed));

    if (!confirmed) {
      // reset program selection and state
      dispatch({
        type: 'LoanApplication/updateValue',
        payload: {
          ...loanApplication,
          programName: '',
          selectedProgram: {},
        },
      });
      dispatch(clearSelectedProgramStatus());
      dispatch(clearCheckboxes());
    } else {
      dispatch(setAcceptedCreditUnion(true));
      dispatch(setHasSeenCUModal(true));
      if (hasTradelines) {
        dispatch(setOpenDebtConsolidationModal(true));
      }
    }
  };

  const handleDebtConsolidationModalClose = (confirmed: boolean) => {
    dispatch(setAcceptedDebtConsolidation(confirmed));
    dispatch(setHasSeenDebtConsolidationModal(true));
  };

  const handleSetSelectedProgram = (value: number[]) => {
    dispatch(setSelectedProgram(value));
  };

  const isDisabledNext =
    Object.keys(loanApplication?.selectedProgram || {}).length === 0 ||
    loanApplication?.selectedProgram?.programName === '' ||
    !!(loanApplication?.selectedProgram?.programName?.includes('Credit Union') && !acceptedCreditUnion) ||
    isReprice;

  return (
    <>
      <Wrapper $isMobile={isMobile} $padding="0rem">
        {openCreditUnionModal && (
          <ConfirmationModal
            openModal={!isReprice && openCreditUnionModal}
            handleModalClose={handleCreditUnionModalClose}
            setOpenModal={(value: boolean) => dispatch(setOpenCreditUnionModal(value))}
            infoModal={templateConfig?.pages?.vmo?.vmoV2?.modal?.helocCreditUnionVerbiage}
          />
        )}
        <ProgramSelector />
        <SubtitleContainer>
          <TextBlock
            text={templateConfig?.pages?.vmo?.vmoV2?.titleProgramSection?.text || pages?.vmo?.vmoV2?.titleProgramSection?.text}
            direction={'center'}
            type={TypeOfText.Subtitle}
            isHidden={templateConfig?.pages?.vmo?.vmoV2?.titleProgramSection?.isHidden || false}
          />
          <TextBlock
            text={templateConfig?.pages?.vmo?.vmoV2?.subtitleProgramSection?.text || pages?.vmo?.vmoV2?.subtitleProgramSection?.text}
            direction={'center'}
            type={TypeOfText.Subtitle}
            isHidden={templateConfig?.pages?.vmo?.vmoV2?.subtitleProgramSection?.isHidden || false}
          />
        </SubtitleContainer>
        <CardsContainer>
          <CalculateAmount selectedProgram={selectedProgram} />
          <ProgramCard
            isReprice={isReprice}
            setSelectedProgram={handleSetSelectedProgram}
            selectedProgram={selectedProgram}
            acceptCreditUnion={acceptedCreditUnion}
            isCreditUnion={isCreditUnion}
          />
        </CardsContainer>
      </Wrapper>
      {allowDebtConsolidation && openDebtConsolidationModal && (
        <ConfirmationModal
          openModal={!isReprice && openDebtConsolidationModal}
          handleModalClose={handleDebtConsolidationModalClose}
          setOpenModal={(value: boolean) => dispatch(setOpenDebtConsolidationModal(value))}
          infoModal={templateConfig?.pages?.vmo?.vmoV2?.modal?.infoDebtConsolidation || pages?.vmo?.vmoV2?.modal?.infoDebConsolidation}
        />
      )}
      {allowDebtConsolidation && !isReprice && acceptedDebtConsolidation && (
        <DebtConsolidation isReprice={isReprice} acceptCreditUnion={acceptedCreditUnion} isCreditUnion={isCreditUnion} />
      )}
      <NavigationButton
        variant="primary"
        nextButton={templateConfig?.pages?.vmo?.vmoV2?.NavigationButton?.nextButton || 'Next'}
        isBackButton={templateConfig?.pages?.vmo?.vmoV2?.NavigationButton?.backButton?.isHidden || false}
        backButton={templateConfig?.pages?.vmo?.vmoV2?.NavigationButton?.backButton?.text || 'Back'}
        disabled={isDisabledNext}
        type="next"
        onAccess={onAccess}
        navigation={navigate}
        pageOrigin={PageOrigin.ESTIMATED_AVAILABLE_EQUITY}
        currentPage={PageOrigin.VMO}
        destinationPage={PageOrigin.PERSONAL_INFORMATION}
        resetChangeDetection={resetChangeDetection}
        hasDataChanged={hasDataChanged}
      />
      <FooterNewVmo></FooterNewVmo>
    </>
  );
};

export default VmoV2;
